import { Calendar, Download, Mail, Monitor, Trash2, Users } from "lucide-react";

import { Card } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import DateRangePicker from "@/components/ui/DateRangePicker.jsx";
import Tag, { RoleTag } from "@/components/ui/Tag.jsx";
import { Tabs } from "@/components/ui/Display.jsx";
import { formatLastActive } from "@/lib/format.js";
import { initials, avatarTone } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";

/*
 * Who this is, what range is being shown, and the tab strip — the top card of
 * user-detail.html.
 *
 * `online` is passed in rather than computed here. It depends on the CURRENT
 * time, and a component that reads the clock during render returns a different
 * answer every time React happens to re-render it — the page owns that decision
 * so there is one clock reading per data load instead of one per render.
 *
 * It is derived from `lastActive`, not from `isActive`: the latter is whether
 * the ACCOUNT is enabled, which says nothing about whether the person is at
 * their desk. Colouring an enabled-but-offline account green is how a dashboard
 * ends up lying about who is working.
 */

export default function UserHeader({
    user,
    systemInfo,
    teamName,
    lastActive,
    range,
    onRange,
    maxDate,
    tab,
    onTab,
    tabs,
    onExport,
    onDelete,
    canDelete,
    exportDisabled,
    online,
}) {

    return (
        <Card>
            <div className="flex flex-wrap items-start gap-5 p-4.5">
                <span className="relative shrink-0">
                    <span
                        className={cx(
                            "grid size-16 place-items-center rounded-2xl text-xl font-semibold text-white",
                            avatarTone(user.name),
                        )}
                    >
                        {initials(user.name)}
                    </span>
                    <span
                        title={online ? "Active now" : "Not active"}
                        data-presence={online ? "online" : "offline"}
                        className={cx(
                            "absolute -right-0.5 -bottom-0.5 size-4 rounded-full ring-3 ring-white dark:ring-slate-900",
                            online ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600",
                        )}
                    />
                </span>

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                        <h1 className="text-xl font-semibold tracking-tight">{user.name}</h1>
                        <RoleTag role={user.role} />
                        <Tag tone={user.isActive ? "brand" : "slate"}>
                            {user.isActive ? "Active" : "Deactivated"}
                        </Tag>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-slate-500 dark:text-slate-400">
                        <Meta icon={<Mail className="size-4 shrink-0" strokeWidth={1.8} />}>
                            {user.email}
                        </Meta>

                        {teamName && (
                            <Meta icon={<Users className="size-4 shrink-0" strokeWidth={1.8} />}>
                                {teamName}
                            </Meta>
                        )}

                        {systemInfo && (
                            <Meta icon={<Monitor className="size-4 shrink-0" strokeWidth={1.8} />}>
                                {[systemInfo.deviceName, systemInfo.os, systemInfo.osVersion]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </Meta>
                        )}

                        <Meta icon={<Calendar className="size-4 shrink-0" strokeWidth={1.8} />}>
                            Last active{" "}
                            <span
                                data-last-active
                                className={cx(
                                    "font-medium",
                                    online
                                        ? "text-emerald-600 dark:text-emerald-400"
                                        : "text-slate-600 dark:text-slate-300",
                                )}
                            >
                                {formatLastActive(lastActive)}
                            </span>
                        </Meta>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2.5">
                    {/* The shortcuts live INSIDE the picker now — a separate
                        Today / 7 / 30 row beside it would be two controls for
                        one decision, disagreeing about which is selected. */}
                    <DateRangePicker
                        startDate={range.startDate}
                        endDate={range.endDate}
                        max={maxDate}
                        onChange={(next) => onRange({ from: next.startDate, to: next.endDate })}
                        className="w-64"
                        id="user-range"
                    />

                    <Button onClick={onExport} disabled={exportDisabled} data-export>
                        <Download className="size-4" strokeWidth={2} />
                        Export
                    </Button>

                    {/* Only an admin can delete, and never themselves — the API
                        refuses both, so offering the button would be a promise
                        the server breaks. */}
                    {canDelete && (
                        <Button variant="danger" onClick={onDelete} data-delete>
                            <Trash2 className="size-4" strokeWidth={2} />
                            Delete user
                        </Button>
                    )}
                </div>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-800">
                <Tabs value={tab} onChange={onTab} options={tabs} />
            </div>
        </Card>
    );
}

function Meta({ icon, children }) {
    return (
        <span className="flex items-center gap-1.5">
            {icon}
            {children}
        </span>
    );
}
